import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { take } from 'rxjs';
import { DomainRequestService } from '../../services/domain-request.service';
import { DomainRequest, DomainRequestStatus } from '../../shared/models/domain-request.model';
import { DomainRequestStatusOption } from './models/custom-domain-request';

@Component({
    selector: 'app-custom-domain-requests',
    imports: [
        CommonModule,
        FormsModule,
        MatTableModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './custom-domain-requests.component.html',
    styleUrl: './custom-domain-requests.component.scss',
    providers: [DomainRequestService]
})
export class CustomDomainRequestsComponent implements OnInit, AfterViewInit {
    public readonly displayedColumns: string[] = [
        'orgName',
        'requestedDomain',
        'domainType',
        'staticWebAppName',
        'status',
        'createdAt',
        'updatedAt',
    ];
    public readonly statusOptions: DomainRequestStatusOption[] = [
        { value: DomainRequestStatus.NEW, label: 'New' },
        { value: DomainRequestStatus.PENDING, label: 'Pending' },
        { value: DomainRequestStatus.APPROVED, label: 'Approved' },
        { value: DomainRequestStatus.REJECTED, label: 'Rejected' },
        { value: DomainRequestStatus.ERROR, label: 'Error' },
    ];
    public readonly dataSource = new MatTableDataSource<DomainRequest>([]);
    public readonly domainRequestStatus = DomainRequestStatus;
    public loading = true;
    public searchTerm = '';

    @ViewChild(MatSort) public sort!: MatSort;

    constructor(
        private readonly domainRequestService: DomainRequestService,
        private readonly snackBar: MatSnackBar,
        private readonly destroyRef: DestroyRef
    ) {}

    public ngOnInit(): void {
        this.configureDataSource();
        this.loadDomainRequests();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    public applySearch(searchValue: string): void {
        this.searchTerm = searchValue;
        this.dataSource.filter = searchValue.trim().toLowerCase();
    }

    public updateStatus(request: DomainRequest, event: MatSelectChange): void {
        const newStatus = event.value as DomainRequestStatus;
        if (!request.requestId || request.status === newStatus) {
            return;
        }

        const previousStatus = request.status;
        const requestId: string = request.requestId;
        request.status = newStatus;

        this.domainRequestService
            .updateStatus(requestId, newStatus)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.snackBar.open('Domain request status updated', 'Close', {
                        duration: 2500,
                    });
                },
                error: () => {
                    request.status = previousStatus;
                    this.snackBar.open('Unable to update status', 'Close', {
                        duration: 3000,
                    });
                },
            });
    }

    private loadDomainRequests(): void {
        this.domainRequestService
            .getDomainRequests()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (requests) => {
                    this.loading = false;
                    this.dataSource.data = requests;
                },
                error: () => {
                    this.loading = false;
                    this.snackBar.open('Unable to load domain requests', 'Close', {
                        duration: 3000,
                    });
                },
            });
    }

    private configureDataSource(): void {
        this.dataSource.filterPredicate = (request: DomainRequest, filter: string): boolean => {
            const normalizedFilter = filter.trim().toLowerCase();
            const searchableText = [
                request.orgName,
                request.requestedDomain,
                request.status,
                request.domainType,
                request.staticWebAppName ?? '',
            ]
                .join(' ')
                .toLowerCase();

            return searchableText.includes(normalizedFilter);
        };

        this.dataSource.sortingDataAccessor = (item: DomainRequest, property: string): string | number => {
            if (property === 'createdAt') {
                return this.toMillis(item.createdAt);
            }

            if (property === 'updatedAt') {
                return this.toMillis(item.updatedAt);
            }

            if (property === 'status') {
                return item.status ?? '';
            }

            if (property === 'orgName') {
                return item.orgName.toLowerCase();
            }

            if (property === 'requestedDomain') {
                return item.requestedDomain.toLowerCase();
            }

            if (property === 'domainType') {
                return item.domainType;
            }

            return '';
        };
    }

    private toMillis(value: unknown): number {
        if (!value) {
            return 0;
        }

        if (value instanceof Date) {
            return value.getTime();
        }

        if (typeof value === 'object' && value !== null && 'toDate' in value) {
            const maybeTimestamp = value as { toDate: () => Date };
            return maybeTimestamp.toDate().getTime();
        }

        return 0;
    }
}
